/**
===================================================
Module: frontend.src.components.visualizer.ThreeJSScene.tsx
Description: Three.js 3D scene component for VSLAM visualization
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useRef, useEffect, useCallback } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useVisualizer } from "../../contexts/VisualizerContext";
import { VISUALIZER_CONFIG } from "../../types/visualizer";

// Utility functions for value conversion
const convertToInternalValue = (
  uiValue: number,
  range: { min: number; max: number },
): number => {
  const normalizedValue = (uiValue - 1) / 9;
  return range.min + normalizedValue * (range.max - range.min);
};

const ThreeJSScene: React.FC = () => {
  const {
    state,
    setSceneReady,
    setVisualizationReady,
    navigateToKeyframe,
    registerResetCamera,
  } = useVisualizer();
  const { sessionData, visualizationSettings } = state;

  // Three.js refs
  const sceneRef = useRef<HTMLDivElement>(null);
  const sceneInstanceRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const animationFrameRef = useRef<number>();
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const initializeRef = useRef<boolean>(false);

  // Visualization object refs
  const landmarksRef = useRef<THREE.Points | null>(null);
  const frustumsRef = useRef<THREE.Group | null>(null);
  const trajectoryRef = useRef<THREE.Line | null>(null);
  const densePointsRef = useRef<THREE.Points | null>(null);

  // SEPARATE STATE: Track if visualization has been created
  const visualizationCreatedRef = useRef<boolean>(false);

  // Computed internal values - STABLE (no dependencies)
  const sparsePointSize = convertToInternalValue(
    visualizationSettings.sparsePointSizeUI,
    VISUALIZER_CONFIG.SPARSE_POINT_RANGE,
  );
  const densePointSize = convertToInternalValue(
    visualizationSettings.densePointSizeUI,
    VISUALIZER_CONFIG.DENSE_POINT_RANGE,
  );
  const frustumScale = convertToInternalValue(
    visualizationSettings.frustumScaleUI,
    VISUALIZER_CONFIG.FRUSTUM_SCALE_RANGE,
  );

  // Add a ref for the click handler
  const clickHandlerRef = useRef<((event: MouseEvent) => void) | null>(null);

  // Mouse click handler - STABLE
  const onMouseClick = useCallback(
    (event: MouseEvent) => {
      if (!frustumsRef.current) {
        return;
      }

      if (!sessionData?.keyframes.length) {
        return;
      }

      const rect = rendererRef.current!.domElement.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current!);
      const intersects = raycasterRef.current.intersectObject(
        frustumsRef.current,
        true,
      );

      if (intersects.length > 0) {
        const clickedObject = intersects[0].object;
        let clickedKeyframeId: string | null = null;

        frustumsRef.current.children.forEach((frustum) => {
          if (frustum.children.includes(clickedObject)) {
            clickedKeyframeId =
              frustum.userData.keyframe_id?.toString() || null;
          }
        });

        if (clickedKeyframeId !== null) {
          const keyframeIndex = sessionData.keyframes.findIndex((kf) => {
            return kf.keyframe_id === clickedKeyframeId?.toString();
          });

          if (keyframeIndex >= 0) {
            navigateToKeyframe(keyframeIndex);
          }
        }
      }
    },
    [sessionData?.keyframes, navigateToKeyframe],
  );

  // Update the click handler ref whenever the function changes
  useEffect(() => {
    clickHandlerRef.current = onMouseClick;
  }, [onMouseClick]);

  const stableClickHandler = useCallback((event: MouseEvent) => {
    if (clickHandlerRef.current) {
      clickHandlerRef.current(event);
    }
  }, []);

  // Three.js initialization - ONLY RUNS ONCE
  const initializeThreeJS = useCallback(() => {
    if (!sceneRef.current || initializeRef.current) return () => {};

    initializeRef.current = true;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8f9fa);
    sceneInstanceRef.current = scene;

    // Camera setup
    const sceneContainer = sceneRef.current;
    const aspect = sceneContainer.clientWidth / sceneContainer.clientHeight;
    const camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
    camera.position.set(10, 10, 10);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(sceneContainer.clientWidth, sceneContainer.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    sceneContainer.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.enableRotate = true;
    controlsRef.current = controls;

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0x404040, 0.4);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(20, 20, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.3);
    fillLight.position.set(-20, 10, -10);
    scene.add(fillLight);

    // Grid
    const gridHelper = new THREE.GridHelper(20, 20, 0xcccccc, 0xe0e0e0);
    (gridHelper.material as THREE.LineBasicMaterial).transparent = true;
    (gridHelper.material as THREE.LineBasicMaterial).opacity = 0.6;
    scene.add(gridHelper);

    // Axes helper
    const axesHelper = new THREE.AxesHelper(3);
    (axesHelper.material as THREE.LineBasicMaterial).transparent = true;
    (axesHelper.material as THREE.LineBasicMaterial).opacity = 0.7;
    scene.add(axesHelper);

    // Mouse event listener
    renderer.domElement.addEventListener("click", stableClickHandler);

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (controlsRef.current) {
        controlsRef.current.update();
      }

      if (
        rendererRef.current &&
        cameraRef.current &&
        sceneInstanceRef.current
      ) {
        rendererRef.current.render(sceneInstanceRef.current, cameraRef.current);
      }
    };

    // Handle resize
    const handleResize = () => {
      if (sceneRef.current && cameraRef.current && rendererRef.current) {
        const width = sceneRef.current.clientWidth;
        const height = sceneRef.current.clientHeight;

        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
      }
    };

    window.addEventListener("resize", handleResize);
    animate();

    setSceneReady(true);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (renderer.domElement.parentNode === sceneContainer) {
        renderer.domElement.removeEventListener("click", stableClickHandler);
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (controlsRef.current) {
        controlsRef.current.dispose();
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
      }

      if (
        sceneContainer &&
        renderer.domElement &&
        sceneContainer.contains(renderer.domElement)
      ) {
        sceneContainer.removeChild(renderer.domElement);
      }

      initializeRef.current = false;
      visualizationCreatedRef.current = false;
      setSceneReady(false);
    };
  }, [stableClickHandler, setSceneReady]);

  const clearVisualization = useCallback(() => {
    if (!sceneInstanceRef.current) return;

    if (landmarksRef.current) {
      sceneInstanceRef.current.remove(landmarksRef.current);
      landmarksRef.current = null;
    }
    if (frustumsRef.current) {
      sceneInstanceRef.current.remove(frustumsRef.current);
      frustumsRef.current = null;
    }
    if (trajectoryRef.current) {
      sceneInstanceRef.current.remove(trajectoryRef.current);
      trajectoryRef.current = null;
    }
    if (densePointsRef.current) {
      sceneInstanceRef.current.remove(densePointsRef.current);
      densePointsRef.current = null;
    }

    visualizationCreatedRef.current = false;
  }, []);

  const createLandmarks = useCallback(
    (landmarkPoints: any[], pointSize: number, isVisible: boolean) => {
      if (!landmarkPoints || landmarkPoints.length === 0) return;

      const filteredLandmarks = landmarkPoints.filter((landmark) => {
        if (
          landmark.n_fnd === null ||
          landmark.n_fnd === undefined ||
          landmark.n_vis === null ||
          landmark.n_vis === undefined ||
          landmark.n_vis <= 0
        ) {
          return false;
        }
        const ratio = landmark.n_fnd / landmark.n_vis;
        return ratio >= VISUALIZER_CONFIG.LANDMARK_FILTER_THRESHOLD;
      });

      if (filteredLandmarks.length === 0) return;

      const positions: number[] = [];
      const colors: number[] = [];

      filteredLandmarks.forEach((landmark) => {
        const position = landmark.position;
        positions.push(position[0], position[1], position[2]);
        colors.push(0.0, 0.0, 0.0);
      });

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3),
      );
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 3),
      );

      const material = new THREE.PointsMaterial({
        size: pointSize,
        vertexColors: true,
        sizeAttenuation: false,
      });

      landmarksRef.current = new THREE.Points(geometry, material);
      landmarksRef.current.visible = isVisible;
      sceneInstanceRef.current!.add(landmarksRef.current);
    },
    [],
  );

  const createSingleFrustum = useCallback(
    (
      position: number[],
      quaternion: number[],
      scale: number,
      keyframe_id: any,
    ): THREE.Group => {
      const group = new THREE.Group();
      group.userData = { keyframe_id: keyframe_id };

      group.position.set(position[0], position[1], position[2]);

      const quat = new THREE.Quaternion(
        -quaternion[0],
        -quaternion[1],
        -quaternion[2],
        quaternion[3],
      );
      group.setRotationFromQuaternion(quat);

      const frustumGeometry = new THREE.ConeGeometry(
        scale * 0.3,
        scale * 0.4,
        4,
      );
      const frustumMaterial = new THREE.MeshBasicMaterial({
        color: 0x007bff,
        wireframe: true,
        transparent: true,
        opacity: 0.7,
      });

      const frustumMesh = new THREE.Mesh(frustumGeometry, frustumMaterial);
      frustumMesh.rotation.y = Math.PI / 4;
      frustumMesh.rotation.x = Math.PI / 2;
      frustumMesh.rotation.y += Math.PI;
      frustumMesh.position.z = -scale * 0.2;

      group.add(frustumMesh);

      const centerGeometry = new THREE.SphereGeometry(scale * 0.05, 8, 8);
      const centerMaterial = new THREE.MeshBasicMaterial({ color: 0x007bff });
      const centerMesh = new THREE.Mesh(centerGeometry, centerMaterial);
      group.add(centerMesh);

      return group;
    },
    [],
  );

  const createCameraFrustums = useCallback(
    (keyframes: any[], scale: number, isVisible: boolean) => {
      if (!sceneInstanceRef.current || !keyframes || keyframes.length === 0)
        return;

      frustumsRef.current = new THREE.Group();

      keyframes.forEach((keyframe) => {
        const position = keyframe.camera_pose.camera_position;
        const quaternion = keyframe.camera_pose.camera_orientation.quaternion;

        const frustum = createSingleFrustum(
          position,
          quaternion,
          scale,
          keyframe.keyframe_id,
        );
        frustumsRef.current!.add(frustum);
      });

      frustumsRef.current.visible = isVisible;
      sceneInstanceRef.current.add(frustumsRef.current);
    },
    [createSingleFrustum],
  );

  const createTrajectory = useCallback(
    (cameraPoints: number[][], isVisible: boolean) => {
      if (!sceneInstanceRef.current || cameraPoints.length < 2) return;

      const points: THREE.Vector3[] = [];
      cameraPoints.forEach((point) => {
        points.push(new THREE.Vector3(point[0], point[1], point[2]));
      });

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x00ff00,
        linewidth: 2,
      });

      trajectoryRef.current = new THREE.Line(geometry, material);
      trajectoryRef.current.visible = isVisible;
      sceneInstanceRef.current.add(trajectoryRef.current);
    },
    [],
  );

  const createDensePoints = useCallback(
    (densePointsData: any[], pointSize: number, isVisible: boolean) => {
      if (!sceneInstanceRef.current || densePointsData.length === 0) return;

      const positions: number[] = [];
      const colors: number[] = [];

      densePointsData.forEach((densePoint) => {
        const position = densePoint.position;
        let color = densePoint.color || [128, 128, 128];

        positions.push(position[0], position[1], position[2]);

        if (Array.isArray(color) && color.length >= 3) {
          const r = color[0] > 1 ? color[0] / 255.0 : color[0];
          const g = color[1] > 1 ? color[1] / 255.0 : color[1];
          const b = color[2] > 1 ? color[2] / 255.0 : color[2];
          colors.push(r, g, b);
        } else {
          colors.push(0.5, 0.5, 0.5);
        }
      });

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3),
      );
      geometry.setAttribute(
        "color",
        new THREE.Float32BufferAttribute(colors, 3),
      );

      const material = new THREE.PointsMaterial({
        size: pointSize,
        vertexColors: true,
        sizeAttenuation: false,
      });

      densePointsRef.current = new THREE.Points(geometry, material);
      densePointsRef.current.visible = isVisible;
      sceneInstanceRef.current.add(densePointsRef.current);
    },
    [],
  );

  const resetCameraView = useCallback((data?: any) => {
    if (!cameraRef.current || !controlsRef.current) {
      return;
    }

    if (data?.keyframes && data.keyframes.length > 0) {
      const positions = data.keyframes.map(
        (kf: any) => kf.camera_pose.camera_position,
      );

      let centerX = 0,
        centerY = 0,
        centerZ = 0;
      positions.forEach((pos: number[]) => {
        centerX += pos[0];
        centerY += pos[1];
        centerZ += pos[2];
      });

      centerX /= positions.length;
      centerY /= positions.length;
      centerZ /= positions.length;

      let maxDistance = 0;
      positions.forEach((pos: number[]) => {
        const dist = Math.sqrt(
          Math.pow(pos[0] - centerX, 2) +
            Math.pow(pos[1] - centerY, 2) +
            Math.pow(pos[2] - centerZ, 2),
        );
        maxDistance = Math.max(maxDistance, dist);
      });

      const viewDistance = Math.max(maxDistance * 3, 10);
      const center = new THREE.Vector3(centerX, centerY, centerZ);

      cameraRef.current.position.set(
        center.x + viewDistance * 0.7,
        center.y + viewDistance * 0.5,
        center.z + viewDistance * 0.7,
      );

      cameraRef.current.lookAt(center);
      controlsRef.current.target.copy(center);
      controlsRef.current.update();
    } else {
      cameraRef.current.position.set(10, 10, 10);
      cameraRef.current.lookAt(0, 0, 0);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  }, []);

  const createInitialVisualization = useCallback(
    (data: any) => {
      if (!sceneInstanceRef.current || visualizationCreatedRef.current) return;
      setVisualizationReady(false);
      clearVisualization();

      if (data.keyframes && data.landmark_points) {
        createLandmarks(
          data.landmark_points,
          sparsePointSize,
          visualizationSettings.showLandmarks,
        );
        createCameraFrustums(
          data.keyframes,
          frustumScale,
          visualizationSettings.showFrustums,
        );

        const cameraPoints = data.keyframes.map(
          (kf: any) => kf.camera_pose.camera_position,
        );
        createTrajectory(cameraPoints, visualizationSettings.showTrajectory);

        if (data.dense_points && data.dense_points.length > 0) {
          createDensePoints(
            data.dense_points,
            densePointSize,
            visualizationSettings.showDensePoints,
          );
        }

        visualizationCreatedRef.current = true;

        setTimeout(() => {
          resetCameraView(data);
          setVisualizationReady(true);
        }, 200);
      } else {
        setVisualizationReady(true);
      }
    },
    [
      clearVisualization,
      createLandmarks,
      createCameraFrustums,
      createTrajectory,
      createDensePoints,
      resetCameraView,
      setVisualizationReady,
    ],
  ); // NO settings dependencies

  const highlightSelectedFrustum = useCallback((keyframeId: string) => {
    if (!frustumsRef.current) return;

    // Reset all frustums to blue
    frustumsRef.current.children.forEach((frustum) => {
      frustum.children.forEach((child) => {
        if ((child as THREE.Mesh).material) {
          (
            (child as THREE.Mesh).material as THREE.MeshBasicMaterial
          ).color.setHex(0x007bff);
          (
            (child as THREE.Mesh).material as THREE.MeshBasicMaterial
          ).needsUpdate = true;
        }
      });
    });

    // Highlight selected frustum in green
    let found = false;
    frustumsRef.current.children.forEach((frustum) => {
      if (
        frustum.userData.keyframe_id !== undefined &&
        frustum.userData.keyframe_id.toString() === keyframeId.toString()
      ) {
        frustum.children.forEach((child) => {
          if ((child as THREE.Mesh).material) {
            (
              (child as THREE.Mesh).material as THREE.MeshBasicMaterial
            ).color.setHex(0x00ff00);
            (
              (child as THREE.Mesh).material as THREE.MeshBasicMaterial
            ).needsUpdate = true;
          }
        });
        found = true;
      }
    });
  }, []);

  useEffect(() => {
    const cleanup = initializeThreeJS();

    // Register reset camera function
    if (registerResetCamera) {
      registerResetCamera(() => {
        resetCameraView(sessionData?.parsedMapData);
      });
    }

    return cleanup;
  }, []);

  useEffect(() => {
    if (
      state.sceneReady &&
      sessionData?.parsedMapData &&
      !visualizationCreatedRef.current
    ) {
      createInitialVisualization(sessionData.parsedMapData);
    }
  }, [
    state.sceneReady,
    sessionData?.parsedMapData,
    createInitialVisualization,
  ]);

  useEffect(() => {
    if (!visualizationCreatedRef.current) return;

    if (landmarksRef.current) {
      landmarksRef.current.visible = visualizationSettings.showLandmarks;
      if (landmarksRef.current.material instanceof THREE.PointsMaterial) {
        landmarksRef.current.material.size = sparsePointSize;
        landmarksRef.current.material.needsUpdate = true;
      }
    }

    if (frustumsRef.current) {
      frustumsRef.current.visible = visualizationSettings.showFrustums;
    }

    if (trajectoryRef.current) {
      trajectoryRef.current.visible = visualizationSettings.showTrajectory;
    }

    if (densePointsRef.current) {
      densePointsRef.current.visible = visualizationSettings.showDensePoints;
      if (densePointsRef.current.material instanceof THREE.PointsMaterial) {
        densePointsRef.current.material.size = densePointSize;
        densePointsRef.current.material.needsUpdate = true;
      }
    }
  }, [visualizationSettings, sparsePointSize, densePointSize]);

  useEffect(() => {
    if (
      frustumsRef.current &&
      sessionData?.parsedMapData?.keyframes &&
      visualizationCreatedRef.current
    ) {
      sceneInstanceRef.current?.remove(frustumsRef.current);
      createCameraFrustums(
        sessionData.parsedMapData.keyframes,
        frustumScale,
        visualizationSettings.showFrustums,
      );
    }
  }, [frustumScale]);

  useEffect(() => {
    if (
      sessionData?.keyframes &&
      sessionData.keyframes.length > 0 &&
      visualizationCreatedRef.current
    ) {
      const currentKeyframe = sessionData.keyframes[state.currentKeyframeIndex];
      if (currentKeyframe) {
        highlightSelectedFrustum(currentKeyframe.keyframe_id);
      }
    }
  }, [state.currentKeyframeIndex, highlightSelectedFrustum]);

  return (
    <div className="scene-container">
      <div ref={sceneRef} className="scene-canvas" />
    </div>
  );
};

export default ThreeJSScene;
